/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable import/no-cycle */
import { DeleteIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
  Heading,
  IconButton,
  useCallbackRef,
} from '@chakra-ui/react';
import { useCallback, useState } from 'react';
import {
  Control,
  FormState,
  UseFormGetFieldState,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormUnregister,
} from 'react-hook-form';
import { FiArrowDown, FiArrowUp, FiCopy, FiEye, FiEyeOff } from 'react-icons/fi';

import ArrayField from './ArrayField';
import Field from './Field';

interface IProps {
  item: any;
  index: number;
  path?: string;
  field: any;
  control: Control<any, any>;
  formState: FormState<any>;
  trigger: any;
  getFieldState: UseFormGetFieldState<any>;
  register: UseFormRegister<any>;
  unregister: UseFormUnregister<any>;
  getValues: UseFormGetValues<any>;
  setValue: UseFormSetValue<any>;
  fields: any[];
  remove: (index: number) => void;
  move: (from: number, to: number) => void;
  insert: (index: number, item: any) => void;
}

function ArrayItem({
  item,
  index,
  field,
  path = '',
  control,
  formState,
  getFieldState,
  trigger,
  register,
  unregister,
  getValues,
  setValue,
  fields,
  remove,
  move,
  insert,
}: IProps) {
  const [isHidden, setIsHidden] = useState(getValues(`${path}.${index}.hidden`));

  const toggleHide = useCallbackRef(
    (idx: number) => {
      setIsHidden(!getValues(`${path}.${idx}.hidden`));
      setValue(`${path}.${idx}.hidden`, !getValues(`${path}.${idx}.hidden`), {
        shouldDirty: true,
      });
    },
    [path],
  );

  const cloneItem = useCallback((idx: number) => {
    insert(idx + 1, getValues(`${path}.${idx}`));
  }, []);

  return (
    <li className="rhf-form__array-item">
      <Accordion allowToggle mb={5}>
        <AccordionItem border="1px solid lightgray" p={2} opacity={isHidden ? 0.5 : 1}>
          <Flex width="100%" alignItems="center">
            <AccordionButton borderRadius={6}>
              <Heading fontSize={20} textAlign="left" noOfLines={1}>
                {item.name
                  ? item.name
                  : item.action?.id
                  ? item.action?.id
                  : item.type
                  ? `${item.type}${item.title ? ` - ${item.title}` : ''}`
                  : item.action?.type
                  ? `${item.action.type}${
                      item.action?.objectType ? ` ${item.action.objectType}` : ''
                    }`
                  : `
              ${path.replace('body', '').replace(/\./g, ' ')}
              ${field.label} ${index}
              ${!item.name ? '' : `: ${item.name}`}
              ${!item.label ? '' : `: ${item.label}`}
              ${!item.title ? '' : `: ${item.title}`}
              ${!item.oldURL ? '' : `: ${item.oldURL}`}
              ${!item.id ? '' : `: ${item.id}`}
              ${!item.journey?.journey ? '' : `: ${item.journey.journey}`}
              `}
              </Heading>
              <AccordionIcon ml="auto" />
            </AccordionButton>
            {!field.canHide ? null : (
              <IconButton
                colorScheme="facebook"
                size="sm"
                fontSize={20}
                ml={3}
                aria-label="Move down"
                icon={isHidden ? <FiEye /> : <FiEyeOff />}
                onClick={() => toggleHide(index)}
              />
            )}
            <IconButton
              colorScheme="facebook"
              size="sm"
              fontSize={20}
              ml={3}
              aria-label="Move down"
              icon={<FiArrowDown />}
              onClick={() => move(index, index + 1)}
              isDisabled={index >= fields.length - 1}
            />
            <IconButton
              colorScheme="facebook"
              size="sm"
              fontSize={20}
              ml={3}
              aria-label="Move up"
              icon={<FiArrowUp />}
              onClick={() => move(index, index - 1)}
              isDisabled={index <= 0}
            />
            <IconButton
              colorScheme="facebook"
              size="sm"
              fontSize={20}
              ml={3}
              aria-label="Clone"
              icon={<FiCopy />}
              onClick={() => cloneItem(index)}
              isDisabled={fields.length >= field.maxLength}
            />
            <IconButton
              colorScheme="red"
              size="sm"
              ml={3}
              aria-label="Delete"
              icon={<DeleteIcon />}
              onClick={() => remove(index)}
            />
          </Flex>
          <AccordionPanel mt={5}>
            {!Array.isArray(field.schema) ? (
              <div />
            ) : (
              field.schema.map((innerField: any) => {
                const newPath = `${path}.${index}.`;
                if (innerField.type === 'array')
                  return (
                    <ArrayField
                      key={newPath + innerField.name}
                      {...{
                        control,
                        register,
                        unregister,
                        formState,
                        field: innerField,
                        getFieldState,
                        getValues,
                        setValue,
                        path: newPath,
                        trigger,
                      }}
                    />
                  );
                return (
                  <Field
                    key={
                      `${newPath}-${innerField.name}${
                        innerField.schema ? `-${innerField.schema.length}` : ''
                      }` || innerField.type
                    }
                    {...{
                      control,
                      register,
                      unregister,
                      field: innerField,
                      getFieldState,
                      getValues,
                      formState,
                      setValue,
                      path: newPath,
                      trigger,
                    }}
                  />
                );
              })
            )}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </li>
  );
}

export default ArrayItem;
